import { Tab, Tabs } from "@mui/material";
import EntaDrawer from "common/components/EntaDrawer";
import useCloseConfirmationStatus from "common/hooks/useCloseConfirmationStatus";
import { EDIT_FORMDEFINITION_TABS } from "developer/formsCRUD/constants";
import { resetStagesEdited } from "developer/formsCRUD/store/formDefinitionStagesSlice";
import { FormDefinition } from "developer/formsCRUD/store/formDefinitionsSlice";
import { Workspace } from "developer/workspaces/model";
import _isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { FormState } from "./Tabs/FormDefinitionTab/FormDefinitionTab";
import FormDefinitionTabPanel from "./Tabs/FormDefinitionTab/FormDefinitionTabPanel";
import StagesTabPanel from "./Tabs/StagesTab/StagesTabPanel";

type EditFormDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  formDefinitionList: FormDefinition[];
  formDefinition: FormDefinition;
  workspaces: Workspace[];
};

const EditFormDrawer = (props: EditFormDrawerProps) => {
  const { isOpen, onClose, formDefinitionList, formDefinition, workspaces } =
    props;
  const [tabSelected, setTabSelected] = useState<number>(0);
  const [leaveOperation, setLeaveOperation] = useState<
    "leaveEdit" | "leaveTab"
  >("leaveEdit");
  const dispatch = useAppDispatch();
  const formRef = useRef<FormState>();

  const { stages, stagesEdited } = useAppSelector(
    (state) => state.developer.stagesCRUD
  );

  const {
    setIsDirty,
    setConfirmationRequested,
    closeConfirmationStatus,
    resetCloseConfirmationStatus,
  } = useCloseConfirmationStatus();

  const hasBeenFormModified = useCallback((): boolean => {
    const hasBeenFormModified = formRef.current?.getFormIsDirty() ?? false;

    return hasBeenFormModified;
  }, []);

  const hasBeenStagesModified = useCallback((): boolean => {
    const hasBeenStagesModified = !_isEqual(stages, stagesEdited);

    return hasBeenStagesModified;
  }, [stages, stagesEdited]);

  const handleChangeTabSelected = useCallback(
    (event: React.ChangeEvent<{}>, newValue: number) => {
      if (
        (tabSelected === EDIT_FORMDEFINITION_TABS.Form &&
          hasBeenFormModified()) ||
        (tabSelected === EDIT_FORMDEFINITION_TABS.Stages &&
          hasBeenStagesModified())
      ) {
        setIsDirty(true);
        setConfirmationRequested();

        setLeaveOperation("leaveTab");
      } else {
        setTabSelected(newValue);
      }
    },
    [
      hasBeenFormModified,
      hasBeenStagesModified,
      tabSelected,
      setIsDirty,
      setConfirmationRequested,
    ]
  );

  const confirmLeaveEditWithoutSave = useCallback(() => {
    if (leaveOperation === "leaveEdit") {
      onClose();
      setTabSelected(EDIT_FORMDEFINITION_TABS.Form);
    } else {
      if (tabSelected === EDIT_FORMDEFINITION_TABS.Form) {
        setTabSelected(EDIT_FORMDEFINITION_TABS.Stages);
      } else {
        dispatch(resetStagesEdited());
        setTabSelected(EDIT_FORMDEFINITION_TABS.Form);
      }
      setLeaveOperation("leaveEdit");
    }
    setIsDirty(false);
  }, [setIsDirty, leaveOperation, onClose, tabSelected, dispatch]);

  const handleClose = useCallback(() => {
    if (hasBeenFormModified() || hasBeenStagesModified()) {
      setIsDirty(true);
      setConfirmationRequested();
      setLeaveOperation("leaveEdit");
    } else {
      confirmLeaveEditWithoutSave();
      setTabSelected(EDIT_FORMDEFINITION_TABS.Form);
    }
  }, [
    confirmLeaveEditWithoutSave,
    hasBeenFormModified,
    hasBeenStagesModified,
    setConfirmationRequested,
    setIsDirty,
  ]);

  useEffect(() => {
    if (closeConfirmationStatus === "closeConfirmed") {
      confirmLeaveEditWithoutSave();
      resetCloseConfirmationStatus();
    }
  }, [
    closeConfirmationStatus,
    confirmLeaveEditWithoutSave,
    resetCloseConfirmationStatus,
  ]);

  if (!isOpen) return null;

  return (
    <EntaDrawer
      width="35%"
      minWidth={350}
      closeButtonProps={{ onClick: () => handleClose() }}
      titulo="Edit Form Definition"
      subtitulo={formDefinition.name}
    >
      <>
        <Tabs
          value={tabSelected}
          onChange={handleChangeTabSelected}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Form" />
          <Tab label="Stages" />
        </Tabs>
        <FormDefinitionTabPanel
          tabSelected={tabSelected}
          formDefinition={formDefinition}
          workspaces={workspaces}
          onClose={onClose}
          onMount={(formState) => {
            formRef.current = formState;
          }}
        />
        <StagesTabPanel
          tabSelected={tabSelected}
          formDefinitionList={formDefinitionList}
          formDefinition={formDefinition}
          onClose={onClose}
        />
      </>
    </EntaDrawer>
  );
};

export default EditFormDrawer;
